import React, { useState, useEffect, useRef, useCallback } from "react";
import { WaterfallVisualization } from "./components/WaterfallVisualization";
import { Event, Agent, Workflow, WebSocketMessage } from "../shared/types";
import { API_ENDPOINTS, config, debugLog, debugError } from "../shared/config";

interface WorkflowState {
  workflow?: Workflow;
  agents: Agent[];
  events: Event[];
  isConnected: boolean;
  isRunning: boolean;
}

export const RealApp: React.FC = () => {
  const [workflowState, setWorkflowState] = useState<WorkflowState>({
    agents: [],
    events: [],
    isConnected: false,
    isRunning: false,
  });

  const [objective, setObjective] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  // const animationRef = useRef<number>();

  // Auto-advance timeline when playing
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentTime((prev) => {
        const maxTime =
          Math.max(...workflowState.events.map((e) => e.timestamp), 0) + 2;
        const next = prev + 0.1 * speed;
        if (next >= maxTime) {
          setIsPlaying(false);
          return maxTime;
        }
        return next;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isPlaying, speed, workflowState.events]);

  // WebSocket connection management
  const connectWebSocket = useCallback((workflowId: string) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = `${API_ENDPOINTS.websocket}/${workflowId}?userId=user-${Date.now()}`;
    debugLog("Connecting to WebSocket:", wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      debugLog("WebSocket connected successfully");
      setWorkflowState((prev) => ({ ...prev, isConnected: true }));
      setError(null);
    };

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        debugLog("WebSocket message received:", message.type, message);
        handleWebSocketMessage(message);
      } catch (err) {
        debugError("Error parsing WebSocket message:", err, event.data);
      }
    };

    ws.onclose = () => {
      debugLog("WebSocket disconnected");
      setWorkflowState((prev) => ({ ...prev, isConnected: false }));
    };

    ws.onerror = (error) => {
      debugError("WebSocket error:", error);
      setError("WebSocket connection failed");
    };

    wsRef.current = ws;
  }, []);

  const handleWebSocketMessage = (message: WebSocketMessage) => {
    switch (message.type) {
      case "workflow.state":
        if (message.data) {
          const data = message.data as {
            workflow: Workflow;
            agents: Agent[];
            events: Event[];
          };
          setWorkflowState((prev) => ({
            ...prev,
            workflow: data.workflow,
            agents: data.agents || [],
            events: data.events || [],
          }));
        }
        break;

      case "workflow.started":
        setWorkflowState((prev) => ({
          ...prev,
          workflow: message.data as Workflow,
          isRunning: true,
        }));
        setIsPlaying(true);
        setCurrentTime(0);
        break;

      case "workflow.completed":
      case "workflow.failed":
      case "workflow.cancelled":
        setWorkflowState((prev) => ({
          ...prev,
          workflow: message.data as Workflow,
          isRunning: false,
        }));
        break;

      case "agent.spawned":
        const agent = message.data as Agent;
        setWorkflowState((prev) => ({
          ...prev,
          agents: [...prev.agents.filter((a) => a.id !== agent.id), agent],
        }));
        break;

      case "event.created":
        setWorkflowState((prev) => ({
          ...prev,
          events: [...prev.events, message.data as Event],
        }));
        break;

      case "connection.established":
        console.log("Connection established:", message.data);
        break;

      default:
        console.log("Unhandled message type:", message.type);
    }
  };

  const createWorkflow = async () => {
    if (!objective.trim()) {
      setError("Please enter a workflow objective");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.createWorkflow, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          objective: objective.trim(),
          userId: `user-${Date.now()}`,
          name: "AI Agent Workflow",
          description: "Real-time AI agent orchestration",
        }),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as any;
        throw new Error(errorData.error || "Failed to create workflow");
      }

      const result = (await response.json()) as any;
      if (result.success && result.data) {
        const workflow = result.data;
        setWorkflowState((prev) => ({
          ...prev,
          workflow,
          agents: [],
          events: [],
        }));

        // Connect WebSocket first
        connectWebSocket(workflow.id);

        // Auto-start the workflow after WebSocket connection
        setTimeout(() => startWorkflow(workflow.id), 2000);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create workflow",
      );
      console.error("Error creating workflow:", err);
    } finally {
      setIsCreating(false);
    }
  };

  const startWorkflow = async (workflowId: string) => {
    try {
      debugLog("Starting workflow:", workflowId);
      const response = await fetch(API_ENDPOINTS.startWorkflow(workflowId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = (await response.json()) as any;
        throw new Error(errorData.error || "Failed to start workflow");
      }

      const result = (await response.json()) as any;

      if (result.success && result.data) {
        setWorkflowState((prev) => ({
          ...prev,
          workflow: result.data,
          isRunning: result.data.status === "running",
        }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start workflow");
      debugError("Error starting workflow:", err);
    }
  };

  const stopWorkflow = async () => {
    if (!workflowState.workflow) return;

    try {
      const response = await fetch(
        API_ENDPOINTS.stopWorkflow(workflowState.workflow.id),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = (await response.json()) as any;
        throw new Error(errorData.error || "Failed to stop workflow");
      }

      console.log("Workflow stopped successfully");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to stop workflow");
      console.error("Error stopping workflow:", err);
    }
  };

  const handlePlay = () => {
    const maxTime =
      Math.max(...workflowState.events.map((e) => e.timestamp), 0) + 2;
    if (currentTime >= maxTime) {
      setCurrentTime(0);
    }
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setCurrentTime(event.timestamp);
    setIsPlaying(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "text-green-500";
      case "completed":
        return "text-blue-500";
      case "failed":
        return "text-red-500";
      case "idle":
        return "text-gray-500";
      default:
        return "text-gray-400";
    }
  };

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const maxTime =
    Math.max(...workflowState.events.map((e) => e.timestamp), 0) + 2;

  return (
    <div className="min-h-screen bg-gradient-to-br from-bg-primary to-bg-secondary overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm border-b border-border-primary">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-white">
              🌊 AI Agent Waterfall
            </h1>
            <div className="flex items-center gap-2">
              <div
                className={`w-2 h-2 rounded-full ${workflowState.isConnected ? "bg-green-500" : "bg-red-500"}`}
              />
              <span className="text-sm text-gray-300">
                {workflowState.isConnected ? "Connected" : "Disconnected"}
              </span>
            </div>
            {workflowState.workflow && (
              <div
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(workflowState.workflow.status)}`}
              >
                {workflowState.workflow.status.toUpperCase()}
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3">
            {workflowState.events.length > 0 && (
              <>
                <button
                  onClick={handlePlay}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                  {isPlaying ? "⏸️" : "▶️"}
                </button>
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  🔄
                </button>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-300">Speed:</label>
                  <input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.5"
                    value={speed}
                    onChange={(e) => setSpeed(parseFloat(e.target.value))}
                    className="w-16"
                  />
                  <span className="text-sm text-gray-300 w-8">{speed}x</span>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-300">Zoom:</label>
                  <input
                    type="range"
                    min="50"
                    max="200"
                    step="25"
                    value={zoom}
                    onChange={(e) => setZoom(parseInt(e.target.value))}
                    className="w-16"
                  />
                  <span className="text-sm text-gray-300 w-12">{zoom}%</span>
                </div>

                <div className="text-sm text-gray-300 font-mono">
                  {currentTime.toFixed(1)}s / {maxTime.toFixed(1)}s
                </div>
              </>
            )}

            {workflowState.isRunning && (
              <button
                onClick={stopWorkflow}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Stop
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-20 h-screen flex flex-col">
        {!workflowState.workflow ? (
          /* Workflow Creation */
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-2xl w-full space-y-6">
              <div className="text-center space-y-4">
                <h2 className="text-4xl font-bold text-white">
                  Create AI Workflow
                </h2>
                <p className="text-gray-300 text-lg">
                  Enter an objective and watch real AI agents coordinate to
                  accomplish it
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Workflow Objective
                  </label>
                  <textarea
                    value={objective}
                    onChange={(e) => setObjective(e.target.value)}
                    placeholder="e.g., Create a comprehensive market analysis report for the renewable energy sector including current trends, growth projections, competitive landscape, and investment opportunities"
                    className="w-full p-4 bg-bg-card border border-border-primary rounded-lg text-white placeholder-gray-400 resize-none h-24 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    disabled={isCreating}
                  />
                </div>

                {error && (
                  <div className="p-4 bg-red-900 bg-opacity-50 border border-red-500 rounded-lg text-red-200">
                    {error}
                  </div>
                )}

                <button
                  onClick={createWorkflow}
                  disabled={isCreating || !objective.trim()}
                  className="w-full py-4 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                >
                  {isCreating ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                      Creating AI Workflow...
                    </div>
                  ) : (
                    "Start AI Agent Workflow"
                  )}
                </button>
              </div>

              <div className="text-center text-sm text-gray-400 space-y-2">
                <p>
                  <strong>Real AI Agents:</strong> Powered by Claude 3.5 Sonnet
                </p>
                <p>
                  <strong>Dynamic Orchestration:</strong> Agents spawn and
                  coordinate automatically
                </p>
                <p>
                  <strong>Live Visualization:</strong> Watch agents work in
                  real-time
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Waterfall Visualization */
          <div className="flex-1 relative">
            <WaterfallVisualization
              events={workflowState.events}
              agents={workflowState.agents}
              isPlaying={isPlaying}
              currentTime={currentTime}
              speed={speed}
              zoom={zoom}
              onTimeChange={setCurrentTime}
              onEventClick={handleEventClick}
              className="w-full h-full"
            />

            {/* Event Details Overlay */}
            {selectedEvent && (
              <div className="absolute bottom-4 left-4 right-4 max-w-2xl mx-auto bg-bg-card bg-opacity-95 backdrop-blur-sm border border-border-primary rounded-lg p-6 z-40">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-white">
                    Event Details
                  </h3>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-400">Type:</span>
                      <span className="ml-2 px-2 py-1 rounded text-xs bg-primary-600 text-white">
                        {selectedEvent.type}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-400">Agent:</span>
                      <span className="ml-2 text-white">
                        {workflowState.agents.find(
                          (a) => a.id === selectedEvent.agentId,
                        )?.type || "Unknown"}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-400">Time:</span>
                      <span className="ml-2 text-white font-mono">
                        {selectedEvent.timestamp.toFixed(2)}s
                      </span>
                    </div>
                    {selectedEvent.metadata?.toolName && (
                      <div>
                        <span className="text-sm text-gray-400">Tool:</span>
                        <span className="ml-2 text-white font-mono">
                          {String(selectedEvent.metadata.toolName)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div>
                    <span className="text-sm text-gray-400">Content:</span>
                    <p className="mt-1 text-white text-sm leading-relaxed max-h-32 overflow-y-auto">
                      {selectedEvent.content}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 backdrop-blur-sm border-t border-border-primary p-2">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span className="text-gray-300">
              Agents: {workflowState.agents.length}
            </span>
            <span className="text-gray-300">
              Events: {workflowState.events.length}
            </span>
            {workflowState.workflow && (
              <span className="text-gray-300">
                Workflow: {workflowState.workflow.name}
              </span>
            )}
          </div>
          <div className="text-gray-400">
            Real AI Agent Orchestration System
          </div>
        </div>
      </div>
    </div>
  );
};
